import React, { useEffect } from 'react';
import {
  Avatar,
  Paper,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Button,
  Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

interface IFormInput {
  keyword?: string;
  location?: string;
}

const schema = yup.object().shape({
  keyword: yup.string().optional(),
  location: yup.string().optional(),
});

const HomeSearch: React.FC = () => {
  const muiTheme = useTheme();
  const isXs = useMediaQuery(muiTheme.breakpoints.down('xs'));
  const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      keyword: '',
      location: ''
    }
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const keyword = searchParams.get('keyword') || '';
    const location = searchParams.get('location') || '';
    reset({ keyword, location });
  }, [reset]);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (data.keyword) {
      searchParams.set('keyword', data.keyword);
    } else {
      searchParams.delete('keyword');
      setValue('keyword', "")
    }

    if (data.location) {
      searchParams.set('location', data.location);
    } else {
      searchParams.delete('location');
      setValue('location', "")
    }
    searchParams.delete('page');
    navigate(`/jobs?${searchParams.toString()}&page=1`);
  };

  const textFieldSize = isXs ? 'small' : isMdUp ? 'medium' : 'small';

  return (
    <Box
      component="form"
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <Paper
        elevation={6}
        sx={{
          height: { xs: 50, md: 60 },
          width: { xs: '90%', sm: '80%', md: 500 },
          backgroundColor: "#fff",
          position: 'absolute',
          bottom: -30,
          borderRadius: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px',
        }}
      >
        <Controller
          name="keyword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size={textFieldSize}
              placeholder="Search by keywords"
              variant="outlined"
              error={!!errors.keyword}
              helperText={errors.keyword ? errors.keyword.message : ''}
              sx={{
                flex: 1,
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-root': {
                  padding: 0,
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                },
              }}
            />
          )}
        />
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size={textFieldSize}
              placeholder="Search by location"
              variant="outlined"
              error={!!errors.location}
              helperText={errors.location ? errors.location.message : ''}
              sx={{
                flex: 1,
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-root': {
                  padding: 0,
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Avatar sx={{ backgroundColor: muiTheme.palette.primary.main, height: { xs: 40, md: 50 }, width: { xs: 40, md: 50 } }}>
                      <LocationOnIcon sx={{ fontSize: { xs: '20px', md: '30px' } }} />
                    </Avatar>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Button
          type="submit"
          sx={{
            backgroundColor: muiTheme.palette.primary.main,
            height: { xs: 40, md: 50 },
            width: { xs: 40, md: 50 },
            minWidth: 0,
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: muiTheme.palette.primary.dark,
            },
          }}
        >
          <SearchIcon sx={{ color: '#fff', fontSize: { xs: '20px', md: '30px' } }} />
        </Button>
      </Paper>
    </Box>
  );
};

export default HomeSearch;
